import { useId, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Bed, Close } from '@mui/icons-material';
import { HotelSearchRoomInputWidget } from './HotelSearchRoomInputWidget';
import { RoomSelection, SearchData } from './types';

interface HotelSearchRoomInputProps {
  setSearchRooms: (value: RoomSelection[]) => void;
  searchData: SearchData;
}
export const HotelSearchRoomInput: FC<HotelSearchRoomInputProps> = ({ setSearchRooms, searchData }) => {
  const { t } = useTranslation();
  const id = useId();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<RoomSelection[]>([]);
  const [initial, setInitial] = useState<RoomSelection[]>([]);
  const [error, setError] = useState(false);
  const close = () => setOpen(false);
  const adults = draft.reduce((n, room) => n + room.paxes.filter((age) => age >= 18).length, 0);
  const hasPendingAges = draft.some((room) => room.paxes.some(Number.isNaN));
  const children = draft.reduce((n, room) => n + room.paxes.filter((age) => age < 18 || Number.isNaN(age)).length, 0);
  return (
    <>
      <Button
        variant="outlined"
        startIcon={<Bed />}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          const rooms = searchData.rooms?.length
            ? searchData.rooms.map((room) => ({ paxes: [...room.paxes] }))
            : [{ paxes: [30, 30] }];
          setInitial(rooms);
          setDraft(rooms);
          setError(false);
          setOpen(true);
        }}
        sx={{
          minHeight: 40,
          width: '100%',
          color: 'text.primary',
          borderColor: 'divider',
          '&.Mui-focusVisible, &:focus-visible': {
            outline: '3px solid',
            outlineColor: 'primary.main',
            outlineOffset: 3,
            bgcolor: 'action.focus',
          },
          justifyContent: 'flex-start',
          textAlign: 'left',
        }}
      >
        {searchData.rooms?.length
          ? t('hotels.roomMenu.selectionSummary', {
              rooms: t('hotels.roomMenu.roomCount', { count: searchData.rooms.length }),
              passengers: t('hotels.roomMenu.passengerCount', {
                count: searchData.rooms.reduce((count, room) => count + room.paxes.length, 0),
              }),
            })
          : t('hotels.searcher.room')}
      </Button>
      <Dialog
        open={open}
        onClose={close}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="sm"
        aria-labelledby={`${id}-title`}
        PaperProps={{
          component: 'form',
          onSubmit: (event: React.FormEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if (
              !draft.length ||
              draft.length > 30 ||
              draft.some(
                (room) =>
                  !room.paxes.some((age) => age >= 18) ||
                  room.paxes.length > 6 ||
                  room.paxes.some((age) => !Number.isInteger(age) || age < 0 || age > 100),
              )
            ) {
              setError(true);
              return;
            }
            setSearchRooms(draft.map((room) => ({ paxes: [...room.paxes] })));
            close();
          },
        }}
      >
        <DialogTitle id={`${id}-title`} sx={{ pr: 7 }}>
          {t('hotels.roomMenu.title')}
          <IconButton
            type="button"
            onClick={close}
            aria-label={t('hotels.roomMenu.close')}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {t('hotels.roomMenu.distribute')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <HotelSearchRoomInputWidget
            defaultRooms={initial}
            onRoomsChange={(rooms) => {
              setDraft(rooms);
              setError(false);
            }}
          />
          {error && (
            <Typography role="alert" color="error">
              {t('hotels.roomMenu.invalidRooms')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{ borderTop: 1, borderColor: 'divider', p: 2, gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600} aria-live="polite">
              {t('hotels.roomMenu.selectionSummary', {
                rooms: t('hotels.roomMenu.roomCount', { count: draft.length }),
                passengers: t('hotels.roomMenu.passengerCount', {
                  count: draft.reduce((count, room) => count + room.paxes.length, 0),
                }),
              })}
            </Typography>
            {!hasPendingAges && (
              <Typography variant="body2" color="text.secondary">
                {t('hotels.roomMenu.passengerSummary', {
                  adults: t('hotels.roomMenu.adultCount', { count: adults }),
                  children: t('hotels.roomMenu.childCount', { count: children }),
                })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {t('hotels.roomMenu.ageAtCheckIn')}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} sx={{ ml: 'auto' }}>
            <Button type="button" onClick={close}>
              {t('hotels.roomMenu.cancel')}
            </Button>
            <Button variant="contained" type="submit">
              {t('hotels.roomMenu.apply')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};
